// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Inventory,
  Category,
  ShoppingCart,
  People,
  LocalShipping,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  BarChart, // Add this import for the Reports icon
} from '@mui/icons-material';
import { NavLink } from 'react-router-dom';

const drawerWidth = 240;
const minDrawerWidth = 60;

const Sidebar = ({ orgId }) => {
  const [minified, setMinified] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      const shouldMinify = window.innerWidth <= 600;
      setIsMobile(shouldMinify);
      if (shouldMinify) {
        setMinified(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMinify = () => {
    if (!isMobile) {
      setMinified(!minified);
    }
  };

  return (
    <Drawer
      variant="permanent"
      PaperProps={{ className: 'surface-sidebar' }}
      sx={{
        width: minified ? minDrawerWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: minified ? minDrawerWidth : drawerWidth,
          boxSizing: 'border-box',
          transition: 'width 0.3s',
          overflowX: 'hidden',
          zIndex: (theme) => theme.zIndex.drawer,
          '@media (max-width: 600px)': {
            width: minified ? minDrawerWidth : '100%',
          },
        },
      }}
    >
      <Toolbar />
      <List>
        {/* Home */}
        <Tooltip title="Home" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/`}
          end // Use 'end' instead of exact="true"
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <HomeIcon />
          </ListItemIcon>
          {!minified && <ListItemText primary="Home" />}
        </ListItemButton>
        </Tooltip>

        {/* Products */}
        <Tooltip title="Products" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/products`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <Category />
          </ListItemIcon>
          {!minified && <ListItemText primary="Products" />}
        </ListItemButton>
        </Tooltip>

        {/* Inventory */}
        <Tooltip title="Inventory" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/inventory`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <Inventory />
          </ListItemIcon>
          {!minified && <ListItemText primary="Inventory" />}
        </ListItemButton>
        </Tooltip>
        {/* Customers */}
        <Tooltip title="Customers" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/customers`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <People />
          </ListItemIcon>
          {!minified && <ListItemText primary="Customers" />}
        </ListItemButton>
        </Tooltip>

        {/* Suppliers */}
        <Tooltip title="Suppliers" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/suppliers`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <LocalShipping />
          </ListItemIcon>
          {!minified && <ListItemText primary="Suppliers" />}
        </ListItemButton>
        </Tooltip>

        {/* Orders */}
        <Tooltip title="Orders" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/orders`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <ShoppingCart />
          </ListItemIcon>
          {!minified && <ListItemText primary="Orders" />}
        </ListItemButton>
        </Tooltip>

        {/* Reports - New Navigation Item */}
        <Tooltip title="Reports" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/reports`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <BarChart />
          </ListItemIcon>
          {!minified && <ListItemText primary="Reports" />}
        </ListItemButton>
        </Tooltip>

        {/* Settings */}
        <Tooltip title="Settings" placement="right" disableHoverListener={!minified}>
        <ListItemButton
          component={NavLink}
          to={`/${orgId}/settings`}
          sx={{
            justifyContent: minified ? 'center' : 'flex-start',
            px: minified ? 2.5 : 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: minified ? 0 : 2, justifyContent: 'center', color: 'inherit' }}>
            <SettingsIcon />
          </ListItemIcon>
          {!minified && <ListItemText primary="Settings" />}
        </ListItemButton>
        </Tooltip>

        {/* Add more navigation items as needed */}
      </List>
      {/* Only show toggle button if not on mobile */}
      {!isMobile && (
        <Tooltip title={minified ? 'Expand Sidebar' : 'Minify Sidebar'}>
      <IconButton
            onClick={toggleMinify}
            sx={{
              position: 'absolute',
              bottom: 20,
              left: minified ? `calc(${minDrawerWidth}px/2 - 12px)` : `calc(${drawerWidth}px/2 - 12px)`,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
              transition: 'left 0.3s',
            }}
          >
            {minified ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      )}
    </Drawer>
  );
};

export default Sidebar;
